import { useCallback, useEffect, useRef, useState } from 'react';
import {
    BuildingImportResult,
    ImportProgress,
    ParsedBuildingBundle,
    getBuildingImportPreview,
    parseBuildingBundle,
    runBuildingImport,
} from '../../api/buildingTransferApi';
import { BuildingImportPreview } from '../../utils/buildingTransfer/importLogic';

export type BuildingImportPhase = 'idle' | 'loadingPreview' | 'preview' | 'importing' | 'done';

interface UseBuildingImportReturn {
    phase: BuildingImportPhase;
    preview: BuildingImportPreview | null;
    remapAllToMe: boolean;
    setRemapAllToMe: (value: boolean) => void;
    confirmOverwrite: boolean;
    setConfirmOverwrite: (value: boolean) => void;
    progress: ImportProgress | null;
    result: BuildingImportResult | null;
    error: string | null;
    selectFile: (file: File) => Promise<void>;
    runImport: () => Promise<void>;
    reset: () => void;
}

const writeErrorMessage = (err: unknown): string => {
    const message = err instanceof Error ? err.message : String(err);
    if (message.toUpperCase().includes('PERMISSION_DENIED')) {
        return 'Åtkomst nekad — kontrollera databasreglerna för denna sökväg';
    }
    return message || 'Importen misslyckades';
};

export const useBuildingImport = (): UseBuildingImportReturn => {
    const [phase, setPhase] = useState<BuildingImportPhase>('idle');
    const [preview, setPreview] = useState<BuildingImportPreview | null>(null);
    const [remapAllToMe, setRemapAllToMe] = useState(false);
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);
    const [progress, setProgress] = useState<ImportProgress | null>(null);
    const [result, setResult] = useState<BuildingImportResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const bundleRef = useRef<ParsedBuildingBundle | null>(null);

    const closeBundle = useCallback(() => {
        bundleRef.current?.close().catch(() => undefined);
        bundleRef.current = null;
    }, []);

    // The ZIP reader holds the File open for lazy binary reads — release it on unmount
    useEffect(() => closeBundle, [closeBundle]);

    const reset = useCallback(() => {
        closeBundle();
        setPhase('idle');
        setPreview(null);
        setRemapAllToMe(false);
        setConfirmOverwrite(false);
        setProgress(null);
        setResult(null);
        setError(null);
    }, [closeBundle]);

    const selectFile = useCallback(async (file: File) => {
        closeBundle();
        setError(null);
        setPreview(null);
        setResult(null);
        setConfirmOverwrite(false);
        setPhase('loadingPreview');
        try {
            const bundle = await parseBuildingBundle(file);
            bundleRef.current = bundle;
            setPreview(await getBuildingImportPreview(bundle));
            setPhase('preview');
        } catch (err) {
            console.error('Error reading building export file:', err);
            closeBundle();
            setError(err instanceof Error ? err.message : 'Filen kunde inte läsas');
            setPhase('idle');
        }
    }, [closeBundle]);

    const runImport = useCallback(async () => {
        const bundle = bundleRef.current;
        if (!bundle || phase !== 'preview') return;
        setPhase('importing');
        setError(null);
        try {
            const importResult = await runBuildingImport(bundle, { remapAllToMe }, setProgress);
            setResult(importResult);
            setPhase('done');
        } catch (err) {
            console.error('Error importing building:', err);
            setError(writeErrorMessage(err));
            setPhase('preview');
        } finally {
            setProgress(null);
        }
    }, [phase, remapAllToMe]);

    return {
        phase,
        preview,
        remapAllToMe,
        setRemapAllToMe,
        confirmOverwrite,
        setConfirmOverwrite,
        progress,
        result,
        error,
        selectFile,
        runImport,
        reset,
    };
};
